import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../arca_Auth/AuthContext';

// Enhanced RBAC system
const hasPermission = (user, permission) => {
  // If user has permissions array, check for specific permission
  if (user.permissions && Array.isArray(user.permissions)) {
    if (user.permissions.includes(permission)) return true;
    if (user.permissions.includes('*')) return true;
  }
  
  // Default role-based permissions
  const rolePermissions = {
    'super_admin': '*',
    'admin': ['users.*', 'members.*', 'events.*'],
    'professional_member': ['events.view', 'community.view'],
    'student_member': ['events.view', 'community.view'],
  };
  
  const userPermissions = rolePermissions[user.userType] || [];
  
  if (userPermissions === '*') return true;
  if (userPermissions.includes(permission)) return true;
  
  return false;
};

const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredPermission = null,
  minRoleLevel = 0 
}) => {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle logout function
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect to login with return URL if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check required role if specified
  if (requiredRole) {
    // Handle multiple roles
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    
    // Check if user's role is in allowed roles
    const userRole = user.userType || user.role;
    const hasRequiredRole = allowedRoles.some(role => {
      if (role === 'admin') {
        // Admin role includes both 'admin' and 'super_admin'
        return userRole === 'admin' || userRole === 'super_admin';
      }
      return userRole === role;
    });
    
    if (!hasRequiredRole) {
      // Redirect to appropriate dashboard based on user's actual role
      const redirectPath = userRole === 'admin' || userRole === 'super_admin' ? '/admin' :
                          userRole === 'country_director' ? '/director' : '/dashboard';
      return <Navigate to={redirectPath} replace />;
    }
  }

  // Pass user and logout function as props to children
  const childrenWithProps = React.cloneElement(children, { user, onLogout: handleLogout });
  
  return childrenWithProps;
};

export default ProtectedRoute;